import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationBySmsCodeForm } from '../components/ConfirmationBySmsCodeForm';
import { SuccessForm } from '../components/SuccessFrom';
import { Stepper } from '../../../commons/Stepper';
import { LocalStorageItems } from '../../../../models/Common';
import { authorizationApi } from '../../../../services/apiServices/user/authorization';
import { useTimer } from '../../../../hooks/useTimer';
import { useCustomSnackbar } from '../../../../hooks/useCustomEnqueueSnackbar';
import { SetNewPasswordWithoutConfirm } from '../../../forms/SetNewPasswordWithoutConfirm';
import { getErrorMessage } from '../../../../utils';

interface PasswordRecoveryByPhoneProps {
  handleBack: () => void;
  handleComplete: () => void;
}

export const PasswordRecoveryByPhone: FC<PasswordRecoveryByPhoneProps> = ({ handleBack, handleComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [phone] = useState(localStorage.getItem(LocalStorageItems.Phone) || '');
  const [code, setCode] = useState('');
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { t } = useTranslation();
  const [submitNewPasswordAPI, { isLoading: isSubmittingNewPasswordLoading }] =
    authorizationApi.useConfirmPasswordRecoveryMutation();
  const [passwordReset, { isLoading: isRequestCodeLoading }] = authorizationApi.usePasswordResetMutation();
  const { timer, startTimer, resetTimer } = useTimer(60);

  useEffect(() => {
    startTimer();
  }, []);

  const handleResendCode = async (phoneNumber: string) => {
    try {
      await passwordReset({ authId: phoneNumber })
        .unwrap()
        .then(() => {
          resetTimer();
        });
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  const handleCodeSubmit = ({ smsCode }: { smsCode: string }) => {
    setCode(smsCode);
    setActiveStep(activeStep + 1);
  };

  const handleSubmitNewPassword = async (password: { password: string }) => {
    try {
      await submitNewPasswordAPI({
        password: password.password,
        code,
      })
        .unwrap()
        .then(() => {
          setActiveStep(activeStep + 1);
          localStorage.removeItem(LocalStorageItems.Phone);
        });
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
      setActiveStep(0);
    }
  };

  const stepNames = [t('auth.enteringCodeFromSms'), t('auth.newPassword'), t('common.ready')];
  const recoveryMessage = (
    <p className="stepper-text">
      {t('auth.anSmsWasSentToTheNumber')} <strong>+38{phone}</strong> {t('auth.enterTheReceivedCodeAndClick')}{' '}
      <strong>“{t('buttons.continue')}”</strong>
    </p>
  );
  const stepElements = [
    <ConfirmationBySmsCodeForm
      textElement={recoveryMessage}
      handleBack={handleBack}
      handleCodeSubmit={handleCodeSubmit}
      handleResendCode={handleResendCode}
      phoneNumber={phone}
      timer={timer}
    />,
    <SetNewPasswordWithoutConfirm
      isLoading={isRequestCodeLoading || isSubmittingNewPasswordLoading}
      submitNewPassword={handleSubmitNewPassword}
      buttonText={t('buttons.continue')}
    />,
    <SuccessForm
      handleClick={handleComplete}
      additionalText={t('auth.yourPasswordHasBeenSuccessfullyChanged')}
      btnLabel={t('buttons.continue')}
    />,
  ];

  return (
    <Stepper
      title={t('auth.createNewPassword')}
      stepNames={stepNames}
      stepElements={stepElements}
      activeStep={activeStep}
    />
  );
};
